import { useTranslation } from 'react-i18next';

import type { AppEntry } from '@/apps/useApps';
import { AppLink, isMockup } from '@/components/catalog/AppLink';

interface AppGridProps {
  apps: readonly AppEntry[];
  onHide: (slug: string) => void;
}

export function AppGrid({ apps, onHide }: AppGridProps) {
  const { t } = useTranslation();
  return (
    <ul className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {apps.map((app) => (
        <li key={app.slug} className="relative">
          <AppLink
            app={app}
            className="block h-full rounded-lg border bg-white p-4 shadow-sm transition hover:shadow-md focus-visible:outline focus-visible:outline-2 focus-visible:outline-appsol-500"
          >
            <div className="flex items-start gap-3 pr-8">
              {app.icon_url ? (
                <img src={app.icon_url} alt="" className="h-10 w-10 flex-none rounded" />
              ) : (
                <div aria-hidden className="h-10 w-10 flex-none rounded bg-appsol-50 text-appsol-900" />
              )}
              <div className="min-w-0">
                <p className="truncate font-medium text-slate-900">{app.name}</p>
                {app.description && (
                  <p className="line-clamp-2 text-sm text-slate-500">{app.description}</p>
                )}
                <div className="mt-1 flex flex-wrap gap-1">
                  {isMockup(app) && (
                    <span className="inline-block rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
                      {t('home.card.prototype')}
                    </span>
                  )}
                  {app.subcategory && (
                    <span className="inline-block rounded-full bg-appsol-50 px-2 py-0.5 text-xs font-medium text-appsol-900">
                      {app.subcategory}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </AppLink>
          <button
            type="button"
            onClick={() => onHide(app.slug)}
            aria-label={t('home.card.hideAria', { name: app.name })}
            title={t('home.card.hideTitle')}
            className="absolute right-2 top-2 rounded p-1 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-appsol-500"
          >
            <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden>
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 3l18 18M10.477 10.477A3 3 0 0014.523 14.52M9.88 4.24A9.953 9.953 0 0112 4c5.523 0 10 4 10 8-.61 1.224-1.49 2.34-2.585 3.275M6.343 6.343C3.93 7.99 2.388 10.06 2 12c1 2 5.477 8 10 8 1.79 0 3.46-.4 4.97-1.1" />
            </svg>
          </button>
        </li>
      ))}
    </ul>
  );
}
